import { USERS_URL, ME_URL } from "../constants";
import { apiSlice } from "./apiSlice";

export const usersApiSlice = apiSlice.injectEndpoints({ 
  endpoints: (builder) => ({
    verifyEmail: builder.mutation({
      query: (verifyToken) => ({
        url: `${USERS_URL}/verify_e-mail/${verifyToken}`,
      })
    }),
    resendVerification: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/resend_verification_e-mail`,
        method: 'POST',
        body: data
      })
    }),

    authUser: builder.mutation({
      query: (userData) => ({
        url: `${USERS_URL}/sign_in`,
        method: 'POST',
        body: userData
      }),
    }),
    signUp: builder.mutation({ 
      query: (userData) => ({ 
        url: `${USERS_URL}/sign_up`,
        method: 'POST',
        body: userData
      }),
    }),
    userSignOut: builder.mutation({ 
      query: () => ({ 
        url: `${USERS_URL}/sign_out`,
        method: 'POST',
        credentials: 'include'
      }),
    }),
    userForgotPassword: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/forgot_password`,
        method: 'POST',
        body: data
      }),
    }),
    userResetPassword: builder.mutation({
      query: ({ token, password }) => ({
        url: `${USERS_URL}/reset_password/${token}`,
        method: 'PUT',
        body: { password }
      }),
    }),
    userReadMyProfile: builder.query({ 
      query: () => ({ 
        url: `${ME_URL}/profile`,
      }),
    }),
    userUpdateMyProfile: builder.mutation({ 
      // userId is only used for cache invalidation; the server uses the session
      query: ({userId, ...body}) => ({ 
        url: `${ME_URL}/profile`,
        method: 'PUT',
        body
      }),
      invalidatesTags: (result, error, { userId }) => [ 
        { type: 'User', id: userId }, 
        { type: 'Users', id: 'LIST' } 
       ]
    }),
    adminCreateUserByAdmin: builder.mutation({
      query: ({adminId, ...body}) => ({
        url: `${USERS_URL}/user/${adminId}/admin/all_users/add_user`,
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'Users', id: 'LIST' }]
    }),
    adminReadAllUsers: builder.query({ 
      query: ({ adminId, page, keyword = '' }) => ({ 
        url: `${USERS_URL}/user/${adminId}/admin/all_users`,
        params: { page, keyword }
      }),
      providesTags: (result) => 
        result?.users 
          ? [ 
              ...result.users.map((u) => ({ type: 'User', id: u._id })), 
              { type: 'Users', id: 'LIST' } 
            ] 
          : [{ type: 'Users', id: 'LIST' }],
    }),
    adminReadUserById: builder.query({ 
      query: ({ adminId, userId }) => ({ 
        url: `${USERS_URL}/user/${adminId}/admin/all_users/user/${userId}`,
      }),
      providesTags: (result, error, { userId }) => [
        { type: 'User', id: userId }
      ],
    }),
    adminUpdateUserById: builder.mutation({
      query: ({ adminId, userId, ...body }) => ({ 
        url: `${USERS_URL}/user/${adminId}/admin/all_users/user/${userId}/edit_user`,
        method: 'PUT',
        body
      }),
      invalidatesTags: (result, error, { userId }) => [ 
        { type: 'User', id: userId }, 
        { type: 'Users', id: 'LIST' } 
       ]
    }),
    adminDeleteUserById: builder.mutation({ 
      query: ({ adminId, userId }) => ({ 
        url: `${USERS_URL}/user/${adminId}/admin/all_users/user/${userId}/delete_user`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { userId }) => [ 
        { type: 'User', id: userId }, 
        { type: 'Users', id: 'LIST' } 
       ],
      keepUnusedDataFor: 5
    })
  }),
});

export const {
  useVerifyEmailMutation,
  useResendVerificationMutation,
  useAuthUserMutation, 
  useSignUpMutation, 
  useUserForgotPasswordMutation,
  useUserResetPasswordMutation, 
  useUserSignOutMutation,
  useUserReadMyProfileQuery, 
  useUserUpdateMyProfileMutation, 
  useAdminCreateUserByAdminMutation,
  useAdminReadAllUsersQuery, 
  useAdminReadUserByIdQuery, 
  useAdminUpdateUserByIdMutation, 
  useAdminDeleteUserByIdMutation 
} = usersApiSlice; 